import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Chip, InputAdornment, CircularProgress, Fab, ToggleButton, ToggleButtonGroup, Tooltip,  } from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { userInfoState } from '../../recoil/atoms';
import client from '../../api/client';
import Pagination from '../../components/Pagination';
import CustomTextField from '../../components/CustomTextField';
import { toPersianDigits } from '../../utils/numberConverter';

const themeColors = {
  dark: '#1a1a2e',
  gold: '#d4af37',
  goldLight: '#f4e4bc',
};

const statusColors = {
  pre_order: 'warning',
  measurement: 'info',
  invoice_ready: 'secondary',
  invoiced: 'primary',
  cutting: 'secondary',
  sewing: 'primary',
  delivery: 'success',
  completed: 'success',
  cancelled: 'error',
};

const statusLabels = {
  pre_order: 'پیش‌سفارش',
  measurement: 'اندازه‌گیری',
  invoice_ready: 'آماده فاکتور',
  invoiced: 'فاکتور شده',
  cutting: 'برش',
  sewing: 'دوخت',
  delivery: 'تحویل',
  completed: 'تکمیل',
  cancelled: 'لغو',
};

const OrdersList = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  
  const navigate = useNavigate();
  const userInfo = useRecoilValue(userInfoState);
  const isSalesperson = userInfo?.role?.toLowerCase() === 'salesperson';

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchOrders();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, statusFilter]);

  useEffect(() => {
    fetchOrders();
  }, [currentPage, pageSize]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await client.get('/orders/', {
        params: {
          page: currentPage,
          page_size: pageSize,
          search,
          status: statusFilter,
        }
      });
      setOrders(response.data.results || []);
      setTotalPages(response.data.total_pages || 1);
      setTotalCount(response.data.total_count || 0);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // چک کنیم آیا این سفارش برای فروشنده قابل کلیک است
  const canClickOrder = (order) => {
    // صاحب فروشگاه همه رو می‌تونه ببینه
    if (!isSalesperson) return true;
    
    // فروشنده فقط پیش‌سفارش‌هایی که خودش ثبت کرده
    const userId = userInfo?.id;
    const isMyOrder = order.created_by === userId;
    const isPreOrder = order.status === 'pre_order';
    
    return isMyOrder && isPreOrder;
  };

  const handleOrderClick = (order) => {
    if (!order || !order.id) return;
    
    if (!canClickOrder(order)) {
      // فروشنده نمی‌تونه کلیک کنه - هیچ کاری نکن
      return;
    }
    
    navigate(`/shop/orders/${order.id}`);
  };

  return (
    <Box sx={{ p: 2, pb: 10 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
        سفارشات
      </Typography>

      {/* جستجو */}
      <CustomTextField         fullWidth
        placeholder="جستجو..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        size="small"
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {/* فیلتر وضعیت */}
      <Box sx={{ mb: 2, overflowX: 'auto' }}>
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={(e, val) => setStatusFilter(val || '')}
          size="small"
        >
          <ToggleButton value="">همه</ToggleButton>
          <ToggleButton value="pre_order">پیش‌سفارش</ToggleButton>
          <ToggleButton value="invoice_ready">آماده فاکتور</ToggleButton>
          <ToggleButton value="sewing">دوخت</ToggleButton>
          <ToggleButton value="completed">تکمیل</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* لیست */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress sx={{ color: themeColors.gold }} />
        </Box>
      ) : orders.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">سفارشی یافت نشد</Typography>
        </Box>
      ) : (
        orders.map((order) => {
          if (!order || !order.id) return null;
          
          const clickable = canClickOrder(order);
          
          return (
            <Card
              key={order.id}
              sx={{
                mb: 1.5,
                cursor: clickable ? 'pointer' : 'default',
                opacity: clickable ? 1 : 0.85,
                '&:hover': clickable ? { boxShadow: 3 } : {},
                bgcolor: clickable ? 'inherit' : '#fafafa',
              }}
              onClick={() => handleOrderClick(order)}
            >
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {order.customer_name || 'بدون نام'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {toPersianDigits(order.order_number || '')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {!clickable && isSalesperson && (
                      <Tooltip title="فقط پیش‌سفارش‌های خودتان قابل ویرایش است">
                        <LockIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                      </Tooltip>
                    )}
                    <Chip
                      size="small"
                      label={statusLabels[order.status] || order.status}
                      color={statusColors[order.status] || 'default'}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {toPersianDigits(order.measurements_count || 0)} اندازه‌گیری
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {order.created_at || ''}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          );
        })
      )}

      {/* صفحه‌بندی */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      )}

      {/* FAB */}
      <Fab
        color="primary"
        sx={{
          position: 'fixed',
          bottom: { xs: 72, md: 16 },
          left: 16,
          bgcolor: themeColors.gold,
          '&:hover': { bgcolor: themeColors.goldLight },
        }}
        onClick={() => navigate('/shop/orders/new')}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default OrdersList;